import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { AuthAction } from './actions/AuthAction';
import { getLicenseRole } from './lib/licenseHelper';
import { evaluateRouteAccess } from './lib/rbacGuard';
import { ROUTES } from './config/routes';
import { isTicketDeepLinkPath } from './lib/ticketRoute';
import { needsIubendaConsent } from './lib/loginLanding';
import { resolveUnauthRedirect, isAdminContextPath } from './lib/loginRedirect';
import ConsentGate from './components/consent/ConsentGate';
// BUG-241 — 인증 판정 경로를 safeStorage 로 통일 (차단 환경 SecurityError 방지).
import { safeGetJSON, safeSetItem } from './lib/safeStorage';

const getCurrentUser = () => safeGetJSON('user');

// F-002 회귀 방지 — 로그인 세션은 있으나 개인정보 처리방침 동의(isIubenda)를 마치지 않은
// 계정이 라우트 가드를 우회(주소창 이동/뒤로가기)해 보호 화면을 쓰는 것을 차단한다.
// 판정은 SSoT needsIubendaConsent(loginLanding.js) 를 그대로 쓴다 — 새 판정 함수 금지.
// 제외 대상은 새 목록을 하드코딩하지 않고, 기존 SSoT resolveUnauthRedirect() 가 null 을
// 반환하는 경로 집합(로그인 경로 + PUBLIC_PATHS — /IubendaLogin 포함)을 재사용한다.
//
// FEAT-052 (#493): 차단 수단이 **리다이렉트에서 모달로** 바뀌었다(이름도 그에 맞춰 고쳤다).
// 가는 곳이 달라졌을 뿐 **판정식과 차단 강도는 그대로다** — 아래 두 가드 모두 동의 전에는
// 보호 컴포넌트를 렌더하지 않는다. 종전 흐름은 전용 화면으로 전체 페이지를 두 번 이동시켜
// 이용자가 "또 로그인했다"로 체감했다.
export const needsIubendaConsentGate = (user, pathname) =>
	needsIubendaConsent(user) && resolveUnauthRedirect(pathname) !== null;

export const AuthRoute = ({ component: Component, roles, ...rest }) => {
	return (
		<Route
			{...rest}
			render={(props) => {
				if (!AuthAction.isLogged()) {
					// IMP-448: 실제 라우트("/Service/:ticketKey?")와 리터럴 불일치로 mailLink 가
					// 저장되지 않던 버그 — 화이트리스트 기반 판정으로 교체.
					// param 이 optional 이라 ticketKey 없는 "/Service" 도 매치된다. 그대로 저장하면
					// 로그인 후 빈 NoRecord 화면에 착지하므로 ticketKey 가 있을 때만 저장한다.
					if (isTicketDeepLinkPath(props.match.path) && props.match.params?.ticketKey) {
						safeSetItem('mailLink', props.match.url);
					}

					return (
						<Redirect
							to={{
								// /pmcf 는 admin 컨텍스트(isAdmin)에서 동작하므로 admin 로그인으로 보낸다.
								pathname: isAdminContextPath(props.location.pathname) ? ROUTES.ADMIN_LOGIN : ROUTES.LOGIN,
								state: { from: props.location },
							}}
						/>
					);
				}
				const user = getCurrentUser();
				// FEAT-025 SPEC-RG-04 — RBAC 가드.
				// FE 가드는 UX 보호 (메뉴/직접 URL 입력 차단). 서버 인가는 별도 (FEAT-025b).
				// 🔴 동의 게이트보다 **먼저** 평가한다(FEAT-052). 게이트는 동의 직후 children 을
				// 곧바로 호출하므로, RBAC 가 뒤에 있으면 권한 없는 라우트의 Component 가 한 번
				// 마운트된다. 거부되면 허용된 경로로 가고, 그 경로가 다시 동의 게이트를 거친다
				// — 동의 전에 열리는 화면은 여전히 없다.
				const role = getLicenseRole(user);
				const decision = evaluateRouteAccess({
					role,
					permAdminAccess: !!user?.permAdminAccess,
					pathname: props.location.pathname,
				});
				if (!decision.allow) {
					return (
						<Redirect
							to={{ pathname: decision.redirectTo, state: { from: props.location } }}
						/>
					);
				}
				// F-002 — 동의 미완료 세션 회수.
				if (needsIubendaConsentGate(user, props.location.pathname)) {
					// 🔴 children 은 **함수**다 — 게이트가 열릴 때만 호출되므로 동의 전에는 보호
					// 컴포넌트의 element 조차 만들어지지 않는다. 모달을 Component 위에 얹으면
					// 뒤 화면이 마운트돼 effect 가 돌고 데이터가 노출된다.
					return <ConsentGate>{() => <Component {...props} />}</ConsentGate>;
				}
				return <Component {...props} />;
			}}
		/>
	);
};

export const AdminAuthRoute = ({ component: Component, roles, ...rest }) => {
	return (
		<Route
			{...rest}
			render={(props) => {
				if (!AuthAction.isLogged()) {
					return (
						<Redirect
							to={{
								pathname: props.location.pathname.toLowerCase().startsWith('/admin/') ? ROUTES.ADMIN_LOGIN : ROUTES.LOGIN,
								state: { from: props.location },
							}}
						/>
					);
				}
				const user = getCurrentUser();
				// RBAC 를 동의 게이트보다 먼저 평가한다 — 이유는 AuthRoute 와 같다.
				const role = getLicenseRole(user);
				const decision = evaluateRouteAccess({
					role,
					permAdminAccess: !!user?.permAdminAccess,
					pathname: props.location.pathname,
				});
				if (!decision.allow) {
					return (
						<Redirect
							to={{ pathname: decision.redirectTo, state: { from: props.location } }}
						/>
					);
				}
				// F-002 — 동의 미완료 세션 회수.
				if (needsIubendaConsentGate(user, props.location.pathname)) {
					return <ConsentGate>{() => <Component {...props} />}</ConsentGate>;
				}
				return <Component {...props} />;
			}}
		/>
	);
};

